import axios from 'axios';
import Cookies from 'js-cookie';
import { PublicClientApplication } from '@azure/msal-browser';

import urls from 'configs/urls';
import authentication from 'configs/authentication';

const { authConfig } = authentication;

const publicClientApplication = new PublicClientApplication({
  auth: {
    authority: authConfig.auth.authority,
    clientId: authConfig.auth.clientId,
    redirectUri: authConfig.auth.redirectUri,
  },
  cache: {
    cacheLocation: authConfig.cache.cacheLocation,
    storeAuthStateInCookie: authConfig.cache.storeAuthStateInCookie,
  },
});

export const parseJwt = token => {
  try {
    let base64String = token.split('.')[1];
    base64String = base64String.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64String)
        .split('')
        .map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch (err) {
    return '';
  }
};

const getApiAuthToken = async azureAuthToken => {
  const data = {
    ApplicationKey: 'app-82038592-7a3e-4ad0-a414-cf21104136c7',
    Token: azureAuthToken,
  };

  try {
    // we call axios directly (not through the 'src/api/request') to prevent circular dependency
    // because we will use an export from this file in 'src/api/request' file so we can't import
    // a method from 'src/api/request' here
    const result = await axios.post(urls.accountManagementUrls.getApiAuthToken, data);
    const resultData = result?.data;
    const token = resultData?.token;
    const roles = resultData?.roles;

    if (token && roles?.length > 0) {
      const apiAuthTokenParsed = parseJwt(token);
      const expiryDate = new Date(0);
      expiryDate.setUTCSeconds(apiAuthTokenParsed.exp);
      const expiryDateFormatted = new Date(expiryDate);

      Cookies.set('apiAuthToken', token, {
        expires: expiryDateFormatted,
      });

      return {
        token,
        roles: apiAuthTokenParsed.roles,
      };
    }

    return {
      token: null,
      roles: [],
    };
  } catch (err) {
    return {
      token: null,
      roles: [],
    };
  }
};

const getAzureAuthToken = async account => {
  const accessTokenRequest = {
    scopes: authConfig.scopes,
    account,
  };

  const result = await publicClientApplication.acquireTokenSilent(accessTokenRequest);
  const { idToken } = result;

  const azureAuthTokenParsed = parseJwt(idToken);
  const expiryDate = new Date(0);
  expiryDate.setUTCSeconds(azureAuthTokenParsed.exp);
  const expiryDateFormatted = new Date(expiryDate);

  Cookies.set('azureAuthToken', idToken, {
    expires: expiryDateFormatted,
  });

  return idToken;
};

const azureSilentLogin = async userEmail => {
  const silentRequest = {
    scopes: authConfig.scopes,
    loginHint: userEmail,
  };

  const msalInstance = publicClientApplication;
  const result = await msalInstance.ssoSilent(silentRequest);
  return result;
};

const azureSilentLogout = async () => {
  const msalInstance = publicClientApplication;
  const account = msalInstance.getAllAccounts()[0];
  const logoutHint = account.idTokenClaims.login_hint;

  await msalInstance.logoutPopup({ logoutHint });
};

const saveRedirectUrl = shouldSaveRedirectUrl => {
  if (shouldSaveRedirectUrl && window.location.href.includes('login') === false)
    sessionStorage.setItem('docNetPortalReturnURL', window.location.href);
};

// to understand the logic of this method please refer to the flowchart in this confluence article:
// https://vezeetard.atlassian.net/wiki/spaces/TEC/pages/2119663621/Azure+AD+Authentication+Flow
export const validateUser = async shouldSaveRedirectUrl => {
  /** This block will get executed if the application cookie exists and valid */
  const apiAuthToken = Cookies.get('apiAuthToken');
  if (apiAuthToken) {
    const apiAuthTokenParsed = parseJwt(apiAuthToken);

    if (apiAuthTokenParsed) {
      const expiryDate = new Date(0);
      expiryDate.setUTCSeconds(apiAuthTokenParsed.exp);
      const expiryDateFormatted = new Date(expiryDate);

      if (expiryDateFormatted > new Date())
        return { isValidUser: true, userRole: apiAuthTokenParsed.Roles, appToken: apiAuthToken };
    }
  }
  /** ####################################################################################### */

  /** This block will get executed if the application cookie doesn't exist or not valid
    but we have a valid azure cookie */
  const azureAuthToken = Cookies.get('azureAuthToken');
  if (azureAuthToken) {
    const azureAuthTokenParsed = parseJwt(azureAuthToken);

    if (azureAuthTokenParsed) {
      const expiryDate = new Date(0);
      expiryDate.setUTCSeconds(azureAuthTokenParsed.exp);
      const expiryDateFormatted = new Date(expiryDate);

      if (expiryDateFormatted > new Date()) {
        const result = await getApiAuthToken(azureAuthToken);

        if (result.token && result.roles.length > 0)
          return { isValidUser: true, userRole: result.roles, appToken: result.token };

        saveRedirectUrl(shouldSaveRedirectUrl);

        return { isValidUser: false };
      }
    }
  }
  /** ####################################################################################### */

  /** This block will get executed if the we don't have azure cookie or it's n't valid
    but we can get user account from cache */
  const account = publicClientApplication.getAllAccounts()[0];

  if (account) {
    try {
      const token = await getAzureAuthToken(account);
      const result = await getApiAuthToken(token);

      if (result.token && result.roles.length > 0)
        return { isValidUser: true, userRole: result.roles, appToken: result.token };

      saveRedirectUrl(shouldSaveRedirectUrl);

      return { isValidUser: false };
    } catch (error) {
      await azureSilentLogout();
      saveRedirectUrl(shouldSaveRedirectUrl);

      return { isValidUser: false };
    }
  }
  /** ####################################################################################### */

  /** This block will get executed if the we can't find user account in cache, but we need first
    to take a step back and see if we ended in this block because the azure cookie didn't exist
    or it was not valid because each one has different flow
    we could have done this check in the previous block but then this block would be duplicated
    because the flow in case of cookie not found or exists but not valid are the same if account
    exists, the flows only differ if no account exists
   */
  let userEmail = Cookies.get('emailAuthValue');
  if (azureAuthToken || userEmail) {
    try {
      if (!userEmail) {
        const azureAuthTokenParsed = parseJwt(azureAuthToken);
        userEmail = azureAuthTokenParsed.email;
      }

      const userAccount = await azureSilentLogin(userEmail);
      const token = await getAzureAuthToken(userAccount);
      const apiToken = await getApiAuthToken(token);

      if (apiToken.token && apiToken.roles.length > 0)
        return { isValidUser: true, userRole: apiToken.roles, appToken: apiToken.token };

      saveRedirectUrl(shouldSaveRedirectUrl);

      return { isValidUser: false };
    } catch (error) {
      // this condition was never tested as it's hard to produce
      if (error?.name.includes('InteractionRequiredAuthError')) {
        try {
          const userAccount = await publicClientApplication.loginPopup({
            scopes: authConfig.scopes,
            prompt: 'select_account',
          });

          const { name, email } = userAccount.idTokenClaims;
          const dateAfterOneWeek = new Date(new Date().setHours(168));
          Cookies.set('nameAuthCookie', name, {
            expires: dateAfterOneWeek,
          });
          Cookies.set('emailAuthCookie', email, {
            expires: dateAfterOneWeek,
          });

          const token = await getAzureAuthToken(userAccount);
          const apiToken = await getApiAuthToken(token);

          if (apiToken.token && apiToken.roles.length > 0)
            return { isValidUser: true, userRole: apiToken.roles, appToken: apiToken.token };

          saveRedirectUrl(shouldSaveRedirectUrl);

          return { isValidUser: false };
        } catch (err) {
          saveRedirectUrl(shouldSaveRedirectUrl);

          return { isValidUser: false };
        }
      } else {
        await azureSilentLogout();
        saveRedirectUrl(shouldSaveRedirectUrl);

        return { isValidUser: false };
      }
    }
  }

  saveRedirectUrl(shouldSaveRedirectUrl);

  return { isValidUser: false };
  /** ####################################################################################### */
};

export const getPublicClientApplicationInstance = () => publicClientApplication;
